import React from 'react';
import {View, Image, Pressable, FlatList, Text} from 'react-native';
import Localized from 'src/constants/AppStrings';
import RoundedAppButton, {RoundedAppButtonType} from '../../RoundedAppButton';
import AVFormattedCurrency from '../../AVFormattedCurrency';
import {
  calculateRecentTotal,
  getMenuItemUrl,
  getModifierImageStyle,
  getModifierTextStyle,
} from './recentOrderUtils';
import {RecentOrderItemType} from 'src/types/RecentOrders';
import {WithLocalizedProps} from 'src/types/PropTypes';
import Styles from 'src/components/Styles';
import AccountStore from 'src/stores/AccountStore';
import RecentOrderItemModifier from './RecentOrderItemModifier';
import {styles} from './recentOrderStyles';

type RecentOrderItemProps = WithLocalizedProps & {
  orderItem: RecentOrderItemType;
  singleCartItem: boolean;
  addItem: (item: RecentOrderItemType) => void;
  productCellPressed: (item: RecentOrderItemType) => void;
  hasAddAllButton?: boolean;
};

const RecentOrderItem: React.FC<RecentOrderItemProps> = ({
  strings,
  singleCartItem,
  orderItem,
  addItem,
  productCellPressed,
  hasAddAllButton,
}) => {
  return (
    <>
      <Pressable
        accessibilityLabel="Add"
        onPress={() =>
          orderItem.available ? productCellPressed(orderItem) : {}
        }
        style={{
          marginVertical: 8,
        }}
        nativeID={'Add-Default-MenuIcon-Image'}
      >
        <View style={styles.itemRowInnerParent}>
          <View style={styles.itemInnerRow}>
            {getMenuItemUrl(orderItem.scancode) == null ? (
              <View style={styles.defaultImageContainer}>
                <Image
                  accessible
                  accessibilityLabel={'Default-MenuIcon Image'}
                  accessibilityRole="image"
                  aria-label="Default-MenuIcon"
                  role="img"
                  style={[
                    styles.sublistImage,
                    getModifierImageStyle(orderItem.modifiers),
                  ]}
                  source={require('src/components/img/DefaultMenuItem.png')}
                  resizeMode="cover"
                />
              </View>
            ) : (
              <Image
                importantForAccessibility="no-hide-descendants"
                role="img"
                source={{
                  uri: getMenuItemUrl(orderItem.scancode),
                }}
                accessibilityRole="image"
                accessible={true}
                accessibilityElementsHidden={true}
                testID="productImage"
                style={[
                  styles.sublistImage,
                  getModifierImageStyle(orderItem.modifiers),
                ]}
                resizeMode="cover"
              />
            )}

            <View style={[styles.itemCenter, getModifierTextStyle(orderItem)]}>
              <Text
                style={styles.item}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
              >
                {orderItem.name}
              </Text>
              {orderItem.modifiers.length > 0 && (
                <FlatList
                  data={orderItem.modifiers}
                  keyExtractor={(itemm) => itemm.saleItemModifierId}
                  renderItem={({item}) => (
                    <RecentOrderItemModifier
                      strings={strings}
                      modifier={item}
                    />
                  )}
                />
              )}
            </View>
          </View>

          <View style={styles.itemRight}>
            {!singleCartItem && orderItem.available && (
              <>
                <RoundedAppButton
                  buttonType={RoundedAppButtonType.Outline}
                  buttonContainerStyle={{marginTop: Styles.Spacing.m2}}
                  buttonViewStyle={{
                    height: 32,
                  }}
                  titleText={'+ ' + Localized.Buttons.add}
                  onPress={() => addItem(orderItem)}
                  accessibilityLabelValue="Add"
                />
                <AVFormattedCurrency
                  style={styles.itemPrice}
                  value={calculateRecentTotal(orderItem)}
                  accessibilityLabel="price"
                  currency={AccountStore.getCurrency()}
                />
              </>
            )}
            {hasAddAllButton && !orderItem.available && (
              <Text
                style={styles.unavailable}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
              >
                {strings.Labels.unavailable}
              </Text>
            )}
          </View>
        </View>
      </Pressable>
    </>
  );
};

export default RecentOrderItem;
